import type { PulseEvent } from './pulseSchema';
import {
  PULSE_BATCH_FLUSH_LOCK_MS,
  PULSE_OUTBOUND_CAP,
  PULSE_OUTBOUND_LOCK_NAME,
  QUEUE_CAP,
} from './pulseConstants';

const FAIL_QUEUE_KEY = 'pulse_event_queue';
const OUTBOUND_KEY = 'pulse_outbound_batch';
const OUTBOUND_LOCK_KEY = 'pulse_outbound_flush_lock';

export { QUEUE_CAP };

export type OutboundBatch = {
  events: PulseEvent[];
  /** UTC ms when the batch became non-empty (shared across tabs for time cap). */
  openedAt: number | null;
};

const safeParse = <T>(raw: string | null, fallback: T): T => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const newId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch (_) {}
  return `pulse_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
};

/** Normalize legacy `{ payload }[]` queue shape → `PulseEvent[]`. */
const normalizeEvents = (raw: unknown): PulseEvent[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (item && typeof item === 'object' && 'payload' in item) {
        return (item as { payload: PulseEvent }).payload;
      }
      return item as PulseEvent;
    })
    .filter((e) => e && typeof e === 'object' && e.event_id);
};

const capTo = (events: PulseEvent[], cap: number): PulseEvent[] => {
  const next = [...events];
  while (next.length > cap) next.shift();
  return next;
};

const capFailQueueEvents = (events: PulseEvent[]): PulseEvent[] =>
  capTo(events, QUEUE_CAP);

const capOutboundEvents = (events: PulseEvent[]): PulseEvent[] =>
  capTo(events, PULSE_OUTBOUND_CAP);

/**
 * Probed once. When localStorage is unavailable (Safari private mode, quota
 * exceeded, blocked cookies) batching cannot persist anything, so callers must
 * fall back to sending immediately rather than writing into a void.
 */
let storageAvailable: boolean | null = null;

export const isStorageAvailable = (): boolean => {
  if (storageAvailable != null) return storageAvailable;
  try {
    const probe = '__pulse_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
    storageAvailable = true;
  } catch {
    storageAvailable = false;
  }
  return storageAvailable;
};

// ——— Fail-retry queue ———

export const getQueue = (): PulseEvent[] => {
  try {
    return normalizeEvents(safeParse(localStorage.getItem(FAIL_QUEUE_KEY), []));
  } catch {
    return [];
  }
};

export const saveQueue = (queue: PulseEvent[]): void => {
  try {
    localStorage.setItem(FAIL_QUEUE_KEY, JSON.stringify(queue));
  } catch (_) {}
};

/** Cap QUEUE_CAP; drop oldest when over capacity. Dedupes by event_id. */
export const enqueue = (event: PulseEvent): void => {
  const queue = getQueue().filter((e) => e.event_id !== event.event_id);
  queue.push(event);
  saveQueue(capFailQueueEvents(queue));
};

export const enqueueMany = (events: PulseEvent[]): void => {
  if (!events.length) return;
  const ids = new Set(events.map((e) => e.event_id));
  const queue = getQueue().filter((e) => !ids.has(e.event_id));
  queue.push(...events);
  saveQueue(capFailQueueEvents(queue));
};

export const clearQueue = (): void => {
  saveQueue([]);
};

export const updateQueue = (updater: (queue: PulseEvent[]) => PulseEvent[]): void => {
  saveQueue(updater(getQueue()));
};

export const removeFromQueueByEventIds = (eventIds: string[]): void => {
  if (!eventIds.length) return;
  const drop = new Set(eventIds);
  updateQueue((queue) => queue.filter((e) => !drop.has(e.event_id)));
};

// ——— Outbound batch (shared across tabs) ———

const emptyOutbound = (): OutboundBatch => ({ events: [], openedAt: null });

/**
 * Reports `openedAt: null` when the stored age is unknown (legacy bare-array
 * record, or a record written without it). Never substitutes `Date.now()`:
 * doing so restarts the time cap on every read, so the batch would never age
 * out and the 30s flush would never fire. Callers treat null as "due now".
 */
export const getOutboundBatch = (): OutboundBatch => {
  try {
    const raw = safeParse<Partial<OutboundBatch> | PulseEvent[] | null>(
      localStorage.getItem(OUTBOUND_KEY),
      null
    );
    if (!raw) return emptyOutbound();

    const legacyArray = Array.isArray(raw);
    const events = normalizeEvents(legacyArray ? raw : raw.events);
    if (!events.length) return emptyOutbound();

    const openedAt = legacyArray ? null : raw.openedAt ?? null;
    return {
      events,
      openedAt: typeof openedAt === 'number' ? openedAt : null,
    };
  } catch {
    return emptyOutbound();
  }
};

/** Persists openedAt exactly as given so no caller can slide the time cap forward. */
export const saveOutboundBatch = (batch: OutboundBatch): void => {
  try {
    const events = capOutboundEvents(batch.events);
    const next: OutboundBatch = {
      events,
      openedAt: events.length ? batch.openedAt : null,
    };
    localStorage.setItem(OUTBOUND_KEY, JSON.stringify(next));
  } catch (_) {}
};

/**
 * Append to outbound batch; stamps openedAt when the batch was empty.
 * No event_id dedupe: ids are `userId + unixSeconds` (a backend contract), so
 * two distinct events in the same second share one id and deduping here would
 * silently discard the second. Removal is by slice in claimOutboundBatch.
 */
export const enqueueOutbound = (event: PulseEvent): OutboundBatch => {
  const batch = getOutboundBatch();
  const events = [...batch.events, event];
  const next: OutboundBatch = {
    events: capOutboundEvents(events),
    openedAt: batch.events.length ? batch.openedAt : Date.now(),
  };
  saveOutboundBatch(next);
  return next;
};

/**
 * Take up to maxEvents off the front of the outbound batch and persist the
 * remainder before any network call, so events arriving during a send join the
 * next batch and a second tab cannot claim the same snapshot.
 * The remainder keeps the original openedAt so leftovers are not pushed into a
 * fresh time window.
 */
export const claimOutboundBatch = (maxEvents: number): PulseEvent[] => {
  const batch = getOutboundBatch();
  if (!batch.events.length) return [];

  const claimed = batch.events.slice(0, maxEvents);
  const remaining = batch.events.slice(maxEvents);
  saveOutboundBatch({
    events: remaining,
    openedAt: remaining.length ? batch.openedAt : null,
  });
  return claimed;
};

export const clearOutboundBatch = (): void => {
  saveOutboundBatch(emptyOutbound());
};

type WebLockManager = {
  request: (
    name: string,
    options: { ifAvailable: boolean },
    callback: (lock: unknown) => unknown
  ) => Promise<unknown>;
};

const getWebLockManager = (): WebLockManager | null => {
  try {
    const locks = (navigator as unknown as { locks?: WebLockManager }).locks;
    return typeof locks?.request === 'function' ? locks : null;
  } catch {
    return null;
  }
};

/** Identifies this tab in the fallback lock so a read-back can pick one winner. */
const TAB_ID = newId();

/**
 * Fallback multi-tab lock, used only when Web Locks is unavailable.
 * localStorage has no compare-and-swap, so after writing we read back and only
 * claim the lock if our own id survived. When two tabs interleave, the later
 * writer wins and the other backs off instead of both proceeding.
 */
export const tryAcquireOutboundLock = (ttlMs: number): boolean => {
  try {
    const now = Date.now();
    const existing = safeParse<{ until: number; owner?: string } | null>(
      localStorage.getItem(OUTBOUND_LOCK_KEY),
      null
    );
    if (existing?.until && existing.until > now) {
      return false;
    }
    localStorage.setItem(
      OUTBOUND_LOCK_KEY,
      JSON.stringify({ until: now + ttlMs, owner: TAB_ID })
    );
    const confirmed = safeParse<{ owner?: string } | null>(
      localStorage.getItem(OUTBOUND_LOCK_KEY),
      null
    );
    return confirmed?.owner === TAB_ID;
  } catch {
    return true;
  }
};

export const releaseOutboundLock = (): void => {
  try {
    localStorage.removeItem(OUTBOUND_LOCK_KEY);
  } catch (_) {}
};

/**
 * Run `fn` while holding the outbound lock, and return its result.
 * Returns null — and only null — when another tab holds the lock, so callers
 * can tell "someone else is sending" from "nothing to send" (`[]`).
 *
 * Prefers Web Locks: it is a true mutex and releases automatically if a tab
 * dies, which a TTL lock cannot do. `ifAvailable` skips rather than queues,
 * since the holding tab is already draining and any remainder is picked up by
 * the scheduler.
 */
export const withOutboundLock = async <T>(fn: () => T): Promise<T | null> => {
  const locks = getWebLockManager();

  if (locks) {
    try {
      return (await locks.request(
        PULSE_OUTBOUND_LOCK_NAME,
        { ifAvailable: true },
        (lock: unknown) => (lock ? fn() : null)
      )) as T | null;
    } catch {
      return null;
    }
  }

  if (!tryAcquireOutboundLock(PULSE_BATCH_FLUSH_LOCK_MS)) return null;
  try {
    return fn();
  } finally {
    releaseOutboundLock();
  }
};

export const OUTBOUND_STORAGE_KEY = OUTBOUND_KEY;
export const FAIL_QUEUE_STORAGE_KEY = FAIL_QUEUE_KEY;

/** UUID helper for view_id / other non-event ids. */
export const createEventId = (): string => newId();

/**
 * Spec: `concat(customer_id, unix_timestamp)` (seconds).
 * Guest / unknown user → prefix `guest`.
 */
export const createPulseEventId = (
  userId: string | number | null | undefined,
  unixTimestampSec: number = Math.floor(Date.now() / 1000)
): string => {
  const id =
    userId !== null && userId !== undefined && String(userId) !== ''
      ? String(userId)
      : 'guest';
  return `${id}${unixTimestampSec}`;
};
